import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { showModal } from "../statusModal/statusModal";
import {
  fetchTicketAPI,
  fetchTicketMessagesAPI,
  fetchTicketsAPI,
  markTicketReadByAdminAPI,
  sendTicketMessageAPI,
  setTicketStatusAPI,
  uploadTicketAttachmentsAPI,
} from "./supportTicketsAPI";

const TICKETS_PAGE_SIZE = 20;
const MESSAGES_PAGE_SIZE = 30;
// The list preview the backend stores for an image-only message
// (ticket.service lastMessagePreview).
export const ATTACHMENT_PREVIEW = "📎 Attachment";

// Whether a ticket with this rating belongs in the list under the rating
// filter ("" = any, "rated", "unrated", or "1"…"5").
const matchesRatingFilter = (filter, rating) => {
  if (!filter) return true;
  if (filter === "rated") return rating != null;
  if (filter === "unrated") return rating == null;
  return Number(filter) === Number(rating);
};

// The filters as one comparable value.
export const filtersKey = (filters) =>
  [filters.status, filters.category, filters.rating, filters.search].join("|");

const initialState = {
  tickets: [],
  total: 0,
  // The list's latest request: its state and the page it asked for (1 = the
  // list itself or new filters, more = scrolling down). Only the latest
  // request's answer is applied, so a slow old filter can't overwrite a newer
  // one.
  listStatus: "idle",
  listError: null,
  listRequestId: null,
  listRequestPage: null,
  filters: { status: "", category: "", search: "", rating: "" },
  // The filters the shown tickets were fetched with.
  listKey: null,
  pagination: { page: 1, limit: TICKETS_PAGE_SIZE, hasMore: true },
  // While the tickets page is open, a live update for a ticket the list
  // doesn't hold fetches that ticket.
  listOpen: false,

  activeTicketId: null,
  // The open ticket as it was opened, kept current by the same live updates,
  // so it stays open when the filters leave it out of the list.
  activeTicket: null,

  // ticketId → { items, status, olderStatus, hasMore, error }
  //   status: the newest page ("idle" | "loading" | "succeeded" | "failed")
  //   olderStatus: scrolling back ("idle" | "loading" | "failed")
  // A reply sent from here is shown at once with status "sending" / "failed"
  // until the server's copy replaces it (keeping its row as localKey).
  threads: {},
  // ticketId → true while closing / reopening it.
  statusPending: {},
  markReadInFlight: {},
};

// ── Helpers ───────────────────────────────────────────────────────────────

const threadOf = (state, ticketId) => {
  if (!state.threads[ticketId]) {
    state.threads[ticketId] = {
      items: [],
      status: "idle",
      olderStatus: "idle",
      hasMore: true,
      error: null,
    };
  }
  return state.threads[ticketId];
};

// Server messages in seq order; replies still on their way (no seq) last.
const bySeq = (a, b) => (a.seq ?? Infinity) - (b.seq ?? Infinity);

// A message from the server (the send's answer or a live echo): it takes the
// place and row of the copy sent from here (same clientMessageId), updates a
// copy already shown, or is added.
const upsertMessage = (thread, message) => {
  const index = thread.items.findIndex(
    (item) =>
      item._id === message._id ||
      (message.clientMessageId && item.clientMessageId === message.clientMessageId),
  );
  if (index === -1) {
    thread.items.push(message);
  } else {
    const current = thread.items[index];
    thread.items[index] = current.status
      ? { ...message, localKey: current._id }
      : { ...current, ...message };
  }
  thread.items.sort(bySeq);
};

const findTicket = (state, ticketId) =>
  state.tickets.find((ticket) => ticket._id === ticketId) ||
  (state.activeTicket?._id === ticketId ? state.activeTicket : null);

// One change to a ticket, in the list and in the open ticket's copy; toTop
// moves it to the top of the list (new activity).
const patchTicket = (state, ticketId, patch, { toTop = false } = {}) => {
  const index = state.tickets.findIndex((ticket) => ticket._id === ticketId);
  if (index !== -1) {
    const ticket = { ...state.tickets[index], ...patch };
    if (toTop) {
      state.tickets.splice(index, 1);
      state.tickets.unshift(ticket);
    } else {
      state.tickets[index] = ticket;
    }
  }
  if (state.activeTicket?._id === ticketId) {
    state.activeTicket = { ...state.activeTicket, ...patch };
  }
};

// Files and previews of replies sent from here, by clientMessageId. They stay
// out of the store (not serializable) until the send succeeds or is dropped;
// a retry reuses them, and their uploaded ids if the upload went through.
const outbox = new Map();

const releaseOutbox = (clientMessageId, delayMs = 0) => {
  const entry = outbox.get(clientMessageId);
  if (!entry) return;
  outbox.delete(clientMessageId);
  // Kept a moment, while the server's copies of the images load in.
  setTimeout(() => entry.previews.forEach((url) => URL.revokeObjectURL(url)), delayMs);
};

// ── Thunks ────────────────────────────────────────────────────────────────

export const fetchTickets = createAsyncThunk(
  "supportTickets/fetchTickets",
  async ({ page = 1 } = {}, { getState, rejectWithValue }) => {
    const { filters, pagination } = getState().supportTickets;
    try {
      const res = await fetchTicketsAPI({ ...filters, page, limit: pagination.limit });
      const { tickets, total, hasMore } = res.data.data;
      return { tickets, total, hasMore, page, key: filtersKey(filters) };
    } catch (error) {
      return rejectWithValue({
        key: filtersKey(filters),
        error: error.response?.data?.message || "Failed to fetch tickets",
      });
    }
  },
);

export const fetchTicket = createAsyncThunk(
  "supportTickets/fetchTicket",
  async (ticketId, { rejectWithValue }) => {
    try {
      const res = await fetchTicketAPI(ticketId);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch ticket");
    }
  },
);

// beforeSeq: the oldest message shown, to load the page before it; none =
// the newest page.
export const fetchTicketMessages = createAsyncThunk(
  "supportTickets/fetchMessages",
  async ({ ticketId, beforeSeq = null }, { rejectWithValue }) => {
    try {
      const params = { limit: MESSAGES_PAGE_SIZE };
      if (beforeSeq != null) params.beforeSeq = beforeSeq;
      const res = await fetchTicketMessagesAPI(ticketId, params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch messages");
    }
  },
);

// Uploads the reply's images (once: a retry reuses their ids), then sends it.
export const sendTicketMessage = createAsyncThunk(
  "supportTickets/sendMessage",
  async ({ ticketId, clientMessageId, text }, { rejectWithValue }) => {
    const entry = outbox.get(clientMessageId) || { files: [], previews: [], imageIds: [] };
    try {
      if (!entry.imageIds) {
        entry.imageIds = entry.files.length
          ? (await uploadTicketAttachmentsAPI(entry.files)).data.data.map((row) => row.imageId)
          : [];
      }
      const res = await sendTicketMessageAPI(ticketId, {
        message: text,
        imageIds: entry.imageIds,
        clientMessageId,
      });
      releaseOutbox(clientMessageId, 3000);
      return res.data.data.message;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to send message");
    }
  },
);

// A reply from the composer: shown in the thread at once (its images from
// local previews), uploaded and sent behind it.
export const queueTicketMessage = ({ ticketId, text, files, senderName }) => (dispatch) => {
  const clientMessageId = crypto.randomUUID();
  const previews = files.map((file) => URL.createObjectURL(file));
  outbox.set(clientMessageId, { files, previews, imageIds: null });
  return dispatch(sendTicketMessage({ ticketId, clientMessageId, text, senderName, previews }));
};

export const retryTicketMessage = ({ ticketId, message }) =>
  sendTicketMessage({
    ticketId,
    clientMessageId: message.clientMessageId,
    text: message.message,
    senderName: message.senderName,
    previews: (message.attachments || []).map((attachment) => attachment.path),
  });

export const setTicketStatus = createAsyncThunk(
  "supportTickets/setStatus",
  async ({ ticketId, status }, { dispatch, rejectWithValue }) => {
    try {
      await setTicketStatusAPI(ticketId, status);
      const label =
        status === "resolved"
          ? "تم إغلاق التذكرة"
          : status === "open"
            ? "تم إعادة فتح التذكرة"
            : "تم تحديث حالة التذكرة";
      dispatch(showModal({ message: label, status: "success" }));
      return { ticketId, status };
    } catch (error) {
      dispatch(showModal({ message: "تعذّر تحديث حالة التذكرة", status: "error" }));
      return rejectWithValue(error.response?.data?.message || "Failed to set status");
    }
  },
);

// One request per ticket at a time: the thread asks again whenever it's at
// the bottom with unread messages.
export const markTicketReadByAdmin = createAsyncThunk(
  "supportTickets/markRead",
  async (ticketId, { rejectWithValue }) => {
    try {
      await markTicketReadByAdminAPI(ticketId);
      return ticketId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to mark read");
    }
  },
  {
    condition: (ticketId, { getState }) =>
      Boolean(ticketId) && !getState().supportTickets.markReadInFlight[ticketId],
  },
);

// ── Slice ─────────────────────────────────────────────────────────────────

const supportTicketsSlice = createSlice({
  name: "supportTickets",
  initialState,
  reducers: {
    openTicket: (state, action) => {
      const ticket = action.payload;
      state.activeTicketId = ticket?._id || null;
      state.activeTicket = ticket || null;
    },
    // The list fetches with the new filters itself (fetchTickets page 1).
    setFilter: (state, action) => {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    clearFilters: (state) => {
      state.filters = { ...initialState.filters };
    },
    setTicketListOpen: (state, action) => {
      state.listOpen = Boolean(action.payload);
    },

    // A reply that failed to send, dropped instead of retried.
    discardUnsentTicketMessage: (state, action) => {
      const { ticketId, clientMessageId } = action.payload;
      const thread = state.threads[ticketId];
      if (!thread) return;
      thread.items = thread.items.filter((item) => item.clientMessageId !== clientMessageId || !item.status);
    },

    // Socket: a message on any ticket (tickets:admin), incl. staff replies
    // from this and other dashboards.
    socketNewMessage: (state, action) => {
      const { ticketId, message } = action.payload || {};
      if (!ticketId || !message) return;

      // Only a thread already read gets it; one opened later reads it anyway.
      const thread = state.threads[ticketId];
      if (thread?.status === "succeeded") upsertMessage(thread, message);

      const ticket = findTicket(state, ticketId);
      if (!ticket) return;
      const fromPlayer = message.senderRole === "user";
      patchTicket(
        state,
        ticketId,
        {
          lastMessage: message.message || (message.attachments?.length ? ATTACHMENT_PREVIEW : ""),
          lastMessageAt: message.createdAt,
          lastSenderRole: message.senderRole,
          // Status comes with the paired ticket:statusUpdate.
          unreadByAdmin: (ticket.unreadByAdmin || 0) + (fromPlayer ? 1 : 0),
        },
        { toTop: true },
      );
    },

    socketStatusUpdate: (state, action) => {
      const { ticketId, status } = action.payload || {};
      if (!ticketId || !status) return;
      patchTicket(state, ticketId, { status });
    },

    // Socket: a brand-new ticket, or one fetched for a live update. It joins
    // the list only if it matches the filters; a search result list stays a
    // search result.
    socketNewTicket: (state, action) => {
      const ticket = action.payload?.ticket;
      if (!ticket?._id) return;
      const index = state.tickets.findIndex((entry) => entry._id === ticket._id);
      if (index !== -1) {
        state.tickets[index] = { ...state.tickets[index], ...ticket };
        return;
      }
      const { status, category, rating, search } = state.filters;
      if (status && status !== ticket.status) return;
      if (category && category !== ticket.category) return;
      if (!matchesRatingFilter(rating, ticket.rating)) return;
      if (search && search.trim()) return;
      state.tickets.unshift(ticket);
      state.total += 1;
    },

    // Socket: the owner rated the ticket.
    socketRating: (state, action) => {
      const { ticketId, rating, ratingFeedback } = action.payload || {};
      if (!ticketId) return;
      patchTicket(state, ticketId, { rating, ratingFeedback: ratingFeedback || "" });
      // Rated while the list shows another rating (or "unrated"): it leaves.
      const index = state.tickets.findIndex((ticket) => ticket._id === ticketId);
      if (index !== -1 && !matchesRatingFilter(state.filters.rating, rating)) {
        state.tickets.splice(index, 1);
        state.total = Math.max(0, state.total - 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTickets.pending, (state, action) => {
        state.listStatus = "loading";
        state.listError = null;
        state.listRequestId = action.meta.requestId;
        state.listRequestPage = action.meta.arg?.page || 1;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        const { tickets, total, hasMore, page, key } = action.payload;
        state.listStatus = "succeeded";
        state.listRequestPage = null;
        state.total = total;
        state.pagination.page = page;
        state.pagination.hasMore = hasMore;
        if (page === 1) {
          state.tickets = tickets;
          state.listKey = key;
        } else {
          const known = new Set(state.tickets.map((ticket) => ticket._id));
          state.tickets.push(...tickets.filter((ticket) => !known.has(ticket._id)));
        }
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.listStatus = "failed";
        state.listError = action.payload?.error || "Failed to fetch tickets";
        // Failed new filters show nothing, not the previous filters' tickets.
        if (state.listRequestPage === 1 && state.listKey !== action.payload?.key) {
          state.tickets = [];
          state.total = 0;
          state.listKey = action.payload?.key ?? null;
          state.pagination.hasMore = false;
        }
      })

      .addCase(fetchTicket.fulfilled, (state, action) => {
        const ticket = action.payload;
        if (!ticket?._id) return;
        patchTicket(state, ticket._id, ticket);
      })

      .addCase(fetchTicketMessages.pending, (state, action) => {
        const { ticketId, beforeSeq } = action.meta.arg;
        const thread = threadOf(state, ticketId);
        if (beforeSeq != null) {
          thread.olderStatus = "loading";
        } else {
          thread.status = "loading";
          thread.error = null;
        }
      })
      .addCase(fetchTicketMessages.fulfilled, (state, action) => {
        const { ticketId, beforeSeq } = action.meta.arg;
        const { messages, hasMore } = action.payload;
        const thread = threadOf(state, ticketId);

        if (beforeSeq != null) {
          thread.olderStatus = "idle";
          // The thread was reloaded meanwhile: this page no longer joins on.
          if (thread.items[0]?.seq !== beforeSeq) return;
          const known = new Set(thread.items.map((item) => item._id));
          thread.items.unshift(...messages.filter((item) => !known.has(item._id)));
          thread.hasMore = hasMore;
          return;
        }

        // The newest page replaces what was shown, keeping what arrived live
        // after it was read and what is still being sent from here.
        const fetched = new Set(messages.map((item) => item._id));
        const newestSeq = messages.length ? messages[messages.length - 1].seq : 0;
        const later = thread.items.filter(
          (item) => !item.status && !fetched.has(item._id) && (item.seq ?? 0) > newestSeq,
        );
        const unsent = thread.items.filter((item) => item.status);
        thread.items = [...messages, ...later, ...unsent];
        thread.hasMore = hasMore;
        thread.status = "succeeded";
        thread.olderStatus = "idle";
      })
      .addCase(fetchTicketMessages.rejected, (state, action) => {
        const { ticketId, beforeSeq } = action.meta.arg;
        const thread = threadOf(state, ticketId);
        if (beforeSeq != null) {
          thread.olderStatus = "failed";
        } else {
          thread.status = "failed";
          thread.error = action.payload || "Failed to fetch messages";
        }
      })

      .addCase(sendTicketMessage.pending, (state, action) => {
        const { ticketId, clientMessageId, text, senderName, previews = [] } = action.meta.arg;
        const thread = threadOf(state, ticketId);
        // A retry goes to the end, as a reply sent now.
        thread.items = thread.items.filter((item) => item.clientMessageId !== clientMessageId);
        thread.items.push({
          _id: clientMessageId,
          clientMessageId,
          ticketId,
          senderRole: "admin",
          senderName: senderName || null,
          message: text,
          attachments: previews.map((path) => ({ _id: path, path })),
          createdAt: new Date().toISOString(),
          status: "sending",
        });
      })
      .addCase(sendTicketMessage.fulfilled, (state, action) => {
        const { ticketId, clientMessageId } = action.meta.arg;
        const saved = action.payload;
        upsertMessage(threadOf(state, ticketId), {
          ...saved,
          clientMessageId: saved.clientMessageId || clientMessageId,
        });
        patchTicket(
          state,
          ticketId,
          {
            lastMessage: saved.message || ATTACHMENT_PREVIEW,
            lastMessageAt: saved.createdAt,
            lastSenderRole: "admin",
            status: "waiting",
          },
          { toTop: true },
        );
      })
      .addCase(sendTicketMessage.rejected, (state, action) => {
        const { ticketId, clientMessageId } = action.meta.arg;
        const item = state.threads[ticketId]?.items.find(
          (entry) => entry.clientMessageId === clientMessageId && entry.status,
        );
        if (item) item.status = "failed";
      })

      .addCase(setTicketStatus.pending, (state, action) => {
        state.statusPending[action.meta.arg.ticketId] = true;
      })
      .addCase(setTicketStatus.fulfilled, (state, action) => {
        const { ticketId, status } = action.payload;
        delete state.statusPending[ticketId];
        patchTicket(state, ticketId, { status });
      })
      .addCase(setTicketStatus.rejected, (state, action) => {
        delete state.statusPending[action.meta.arg.ticketId];
      })

      .addCase(markTicketReadByAdmin.pending, (state, action) => {
        state.markReadInFlight[action.meta.arg] = true;
      })
      .addCase(markTicketReadByAdmin.fulfilled, (state, action) => {
        const ticketId = action.payload;
        delete state.markReadInFlight[ticketId];
        patchTicket(state, ticketId, { unreadByAdmin: 0 });
        const thread = state.threads[ticketId];
        if (thread) {
          thread.items = thread.items.map((item) =>
            item.senderRole === "user" && !item.readByAdmin ? { ...item, readByAdmin: true } : item,
          );
        }
      })
      .addCase(markTicketReadByAdmin.rejected, (state, action) => {
        delete state.markReadInFlight[action.meta.arg];
      });
  },
});

export const {
  openTicket,
  setFilter,
  clearFilters,
  setTicketListOpen,
  discardUnsentTicketMessage,
  socketNewMessage,
  socketStatusUpdate,
  socketRating,
  socketNewTicket,
} = supportTicketsSlice.actions;

export const discardTicketMessage = ({ ticketId, clientMessageId }) => (dispatch) => {
  releaseOutbox(clientMessageId);
  dispatch(discardUnsentTicketMessage({ ticketId, clientMessageId }));
};

// ── Socket thunks ───────────────────────────────────────────────────────────
// They wrap the socket reducers with what a reducer can't do: fetch a ticket
// the open list doesn't hold, so its update still surfaces.

export const ensureTicketLoaded = (ticketId) => async (dispatch, getState) => {
  const state = getState().supportTickets;
  if (!ticketId || !state.listOpen) return;
  if (state.tickets.some((ticket) => ticket._id === ticketId)) return;
  try {
    const res = await fetchTicketAPI(ticketId);
    const ticket = res?.data?.data;
    if (ticket?._id) dispatch(socketNewTicket({ ticket }));
  } catch {
    // It appears on the next refresh.
  }
};

const ensureListed = (ticketId) => (dispatch, getState) => {
  if (ticketId && !getState().supportTickets.tickets.some((ticket) => ticket._id === ticketId)) {
    dispatch(ensureTicketLoaded(ticketId));
  }
};

export const onSocketNewMessage = (payload) => (dispatch) => {
  dispatch(socketNewMessage(payload));
  dispatch(ensureListed(payload?.ticketId));
};

export const onSocketStatusUpdate = (payload) => (dispatch) => {
  dispatch(socketStatusUpdate(payload));
  dispatch(ensureListed(payload?.ticketId));
};

export const onSocketRating = (payload) => (dispatch) => {
  dispatch(socketRating(payload));
  dispatch(ensureListed(payload?.ticketId));
};

export default supportTicketsSlice.reducer;
